import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {StatusPanel, StatusPanelMode} from '@Panels/status-panel';
import {
  useAccountIdsToPublishTo,
  useAllPublishingParamsForAccount,
  usePublishedAccountUrlMap,
  usePublishingParams,
  usePublishingStatusForAccountWithMediaType,
  usePublishingStatusForAllAccounts,
  useRetryPublishingForAccount,
  useSocialMediaWizardPost,
  useStartPublishingForAccount,
} from '@Components/social-media/social-media.hooks';
import {isLoading, LoadingStates} from '@Utils/loading.util';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {getLoadingTextByPublishingAction} from '@Libraries/social-media-wizard-step-library';
import {haveAllPublishingStatesLoaded} from '@Libraries/social-media-wizard-accounts-library';
import {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {isGoogleBusinessProfileAccount} from '@Components/social-media/account.vo';
import type {SocialPost} from '@Components/social-media/post.vo';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import {formatDateFromTimestamp, formatLocalDate} from '@Utils/date.util';
import {
  initPublishedUrlMapForAccounts,
  initPublishingStatusMapForAccounts,
  initRetryPublishingMapForAccount,
  setRetryingStatusForAccountWithMediaType,
  updateTriggerPrevious,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {
  addConnectAccountBroadcastChannel,
  getAccountConnectionResponseFromBroadcast,
  handleAccountConnectionResponse,
  reAuthenticateInModal,
  removeConnectAccountBroadcastChannel,
} from '@Libraries/social-media-account.library';
import {
  goBackToEvent,
  goBackToEventFromOneClickPromotion,
  isEventOneClickPromotionFlow,
  isEventsSocialPostingFlow,
} from '@Libraries/social-media-event-promotion.library';
import {ContinueEditingEvent} from '@Components/social-media-wizard/social-media-wizard.types';
import {useHasSeenConfetti} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {OnboardingConfettiExplosion} from '@Components/onboarding-confetti-explosion/onboarding-confetti-explosion';
import {deleteCookie, readCookie} from '@Utils/cookie.util';
import {CONTENT_PLANNER_POST_COOKIE} from '@Components/content-planner/components/add-post-menu-items';
import {useAppDispatch} from '@/hooks';
import {MY_STUFF_HASHES} from '@Libraries/my-stuff-library';

interface PublishingPostProps {
  account: SocialAccount;
  publishingAction: SocialPostPublishingAction;
  platformMediaType?: PlatformMediaType;
}

const USER_WAITING_MESSAGE_TO_SHOW_TIME_MS = 30000;

export function PublishingPost({account, publishingAction, platformMediaType = PlatformMediaType.POST}: PublishingPostProps): ReactElement<any> | null {
  useStartPublishingForAccount(account.id, platformMediaType);
  useRetryPublishingForAccount(account.id, platformMediaType);
  const loadingState: LoadingStates = usePublishingStatusForAccountWithMediaType(account.id, platformMediaType);
  const post: SocialPost | undefined = useSocialMediaWizardPost();
  const publishingParams = usePublishingParams();
  const publishingStatusForAllAccounts = usePublishingStatusForAllAccounts();
  const publishingParamsForAccount = useAllPublishingParamsForAccount(account.id)[platformMediaType];
  const [retryingAccountId, setRetryingAccountId] = useState(0);
  const publishedAccountUrlMap = usePublishedAccountUrlMap();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const hasSeenConfetti = useHasSeenConfetti();
  const [showConfetti, setShowConfetti] = useState(false);
  const dispatch = useAppDispatch();
  const [showWaitingMessage, setShowWaitingMessage] = useState(false);
  const [waitMessageHandle, setWaitMessageHandle] = useState<number>(0);
  const showsBackToContentPlanner = useRef(false);
  let timeoutHandler: NodeJS.Timeout | null = null;

  useEffect(() => {
    initWaitingMessageAfterPublish();
  }, [loadingState]);

  const initWaitingMessageAfterPublish = (): void => {
    if (waitMessageHandle && loadingState !== LoadingStates.LOADING) {
      clearTimeout(waitMessageHandle);
      setWaitMessageHandle(0);
    } else {
      setupTimerForWaitingMessage();
    }
  };

  const setupTimerForWaitingMessage = (): void => {
    if (!timeoutHandler) {
      timeoutHandler = setTimeout(() => {
        setShowWaitingMessage(true);
      }, USER_WAITING_MESSAGE_TO_SHOW_TIME_MS);
      setWaitMessageHandle(timeoutHandler as unknown as number);
    }
  };

  useEffect(() => {
    dispatch(initRetryPublishingMapForAccount());
    dispatch(initPublishingStatusMapForAccounts());
    dispatch(initPublishedUrlMapForAccounts());
  }, []);

  useEffect(() => {
    const accountCreatedBroadcastChannel = addConnectAccountBroadcastChannel(doFinishAccountRetry);

    return (): void => {
      removeConnectAccountBroadcastChannel(accountCreatedBroadcastChannel);
    };
  }, [retryingAccountId]);

  const doFinishAccountRetry = (broadcastEvent: MessageEvent): void => {
    const connectionResponse = getAccountConnectionResponseFromBroadcast(broadcastEvent);
    handleAccountConnectionResponse(connectionResponse, (): void => {
      if (retryingAccountId !== account.id) {
        return;
      }

      dispatch(
        setRetryingStatusForAccountWithMediaType({
          accountId: account.id,
          platformMediaType,
        })
      );
    });
    return;
  };

  useDidUpdateEffect((): void => {
    if (haveAllPublishingStatesLoaded(accountIdsToPublishTo, publishingStatusForAllAccounts, publishingParams)) {
      if (isEventsSocialPostingFlow()) {
        goBackToEvent();
        return;
      }

      if (isEventOneClickPromotionFlow()) {
        goBackToEventFromOneClickPromotion();
        return;
      }

      if (!hasSeenConfetti && publishingAction !== SocialPostPublishingAction.SAVE) {
        setShowConfetti(true);
        void window.PMW.writeLocal('socialmedia/seenSocialMediaWizardConfetti');
      }
    }
  }, [publishedAccountUrlMap, publishingStatusForAllAccounts]);

  const isMultiAccountPost = (): boolean => {
    if (!post) {
      return false;
    }

    return Object.keys(publishingParams).length > 1;
  };

  const getIconByPublishingAction = (): string => {
    switch (publishingAction) {
      case SocialPostPublishingAction.PUBLISH:
        return 'icon-check';
      case SocialPostPublishingAction.SCHEDULE:
        return 'icon-calendar';
      case SocialPostPublishingAction.SAVE:
        return 'icon-doc';
      default:
        return '';
    }
  };

  const getIconByLoadingState = (): string => {
    switch (loadingState) {
      case LoadingStates.NOT_LOADED:
        return 'icon-exclamation-triangle';
      case LoadingStates.LOADING:
        return '';
      case LoadingStates.LOADED:
        return getIconByPublishingAction();
      default:
        return '';
    }
  };

  const getThemeByLoadingState = (): StatusPanelMode => {
    switch (loadingState) {
      case LoadingStates.NOT_LOADED:
        return StatusPanelMode.ERROR;
      case LoadingStates.LOADING:
      case LoadingStates.LOADED:
      default:
        return getThemeByPublishingAction();
    }
  };

  const shouldShowReturnToContentPlanner = (): boolean => {
    const value = readCookie(CONTENT_PLANNER_POST_COOKIE);

    if (value) {
      deleteCookie(CONTENT_PLANNER_POST_COOKIE);
      showsBackToContentPlanner.current = true;
      return true;
    }

    return showsBackToContentPlanner.current;
  };

  const getSecondaryButtonText = (): string => {
    if (isEventsSocialPostingFlow()) {
      return window.i18next.t('pmwjs_my_event');
    }

    if (!shouldShowReturnToContentPlanner()) {
      return window.i18next.t('pmwjs_my_posts');
    }

    return window.i18next.t('pmwjs_back_content_planner');
  };

  const getThemeByPublishingAction = (): StatusPanelMode => {
    switch (publishingAction) {
      case SocialPostPublishingAction.PUBLISH:
        return StatusPanelMode.PUBLISHING;
      case SocialPostPublishingAction.SCHEDULE:
        return StatusPanelMode.SCHEDULING;
      case SocialPostPublishingAction.SAVE:
        return StatusPanelMode.SAVING;
      default:
        return StatusPanelMode.ERROR;
    }
  };

  const getButtonTextForPublishedPost = (): string => {
    if (isGoogleBusinessProfileAccount(account)) {
      return window.i18next.t('pmwjs_view_profile');
    }

    return window.i18next.t('pmwjs_view_this_post');
  };

  const getRightButtonByPublishingAction = (): ReactElement<any> | undefined => {
    switch (publishingAction) {
      case SocialPostPublishingAction.PUBLISH:
        return (
          <Button
            isFullWidth
            iconPlacement={IconPlacement.RIGHT}
            icon="icon-external-link"
            size={Size.SMALL}
            text={getButtonTextForPublishedPost()}
            type={Type.SUCCESS}
            onClick={onViewPost}
          />
        );
      case SocialPostPublishingAction.SCHEDULE:
        return (
          <Button
            isFullWidth
            customClasses="-noun"
            iconPlacement={IconPlacement.LEFT}
            icon="icon-pencil"
            size={Size.SMALL}
            onClick={onContinueEditing}
            text={window.i18next.t('pmwjs_edit_reschedule')}
            type={Type.SECONDARY}
          />
        );
      case SocialPostPublishingAction.SAVE:
        return <Button isFullWidth size={Size.SMALL} text={window.i18next.t('pmwjs_continue_editing')} type={Type.PRIMARY} onClick={onContinueEditing} />;
      default:
        return undefined;
    }
  };

  const onViewPost = (): void => {
    if (account.id in publishedAccountUrlMap) {
      window.open(publishedAccountUrlMap[account.id][platformMediaType] ?? '', '_blank');
    }
  };

  const onContinueEditing = (): void => {
    window.dispatchEvent(new CustomEvent(ContinueEditingEvent.CONTINUE_EDITING, {}));
    dispatch(updateTriggerPrevious());
  };

  const onRetryPublishing = (): void => {
    setRetryingAccountId(account.id);
    reAuthenticateInModal(account.id, account.type);
  };

  const getRightButton = (): ReactElement<any> | undefined => {
    switch (loadingState) {
      case LoadingStates.NOT_LOADED:
        return (
          <Button
            onClick={onRetryPublishing}
            isFullWidth
            iconPlacement={IconPlacement.LEFT}
            icon="icon-rotate-ccw"
            size={Size.SMALL}
            text={window.i18next.t('pmwjs_retry')}
            type={Type.PRIMARY}
          />
        );
      case LoadingStates.LOADED:
        return getRightButtonByPublishingAction();
      default:
        return undefined;
    }
  };

  const getPublishSuccessText = (): string => {
    return window.i18next.t('pmwjs_your_post_publishing_successfully');
  };

  const getDateString = (dateTimeString: string): string[] => {
    const parts = dateTimeString.split(', ');

    const datePart = `${parts[0]}, ${parts[1]}`;
    const timePart = parts[2];

    return [datePart, timePart];
  };

  const getScheduleContainer = (): string[] | null => {
    if (!post?.publishOn) {
      return null;
    }

    const stringDate = formatLocalDate(post.publishOn);
    const dateTimeArray = getDateString(stringDate);
    const time = dateTimeArray[1];
    const date = formatDateFromTimestamp(post.publishOn);

    return [date, time];
  };

  const getScheduleSuccessText = (): string => {
    const dateTime = getScheduleContainer();

    if (!dateTime) {
      return '';
    }

    const i18nData = {
      date: dateTime[0],
      time: dateTime[1],
    };

    if (isMultiAccountPost()) {
      return window.i18next.t('pmwjs_your_posts_scheduled_successfully', i18nData);
    }

    return window.i18next.t('pmwjs_your_post_scheduled_successfully', i18nData);
  };

  const getSaveSuccessText = (): string => {
    if (isMultiAccountPost()) {
      return window.i18next.t('pmwjs_your_posts_saved_successfully');
    }

    return window.i18next.t('pmwjs_your_post_saved_successfully');
  };

  const onMyPosts = (): void => {
    window.location.href = window.PMW.util.site_url(`posters/mine${MY_STUFF_HASHES.socialPosts}`);
  };

  const onBackToContentPlanner = (): void => {
    window.location.href = window.PMW.util.site_url(`posters/mine${MY_STUFF_HASHES.contentPlanner}`);
  };

  const onClickSecondaryButton = (): void => {
    if (isEventsSocialPostingFlow()) {
      goBackToEvent();
      return;
    }

    if (shouldShowReturnToContentPlanner()) {
      onBackToContentPlanner();
      return;
    }

    onMyPosts();
  };

  const getLeftButton = (): ReactElement<any> => {
    return (
      <Button
        isFullWidth
        iconPlacement={IconPlacement.LEFT}
        icon="icon-arrow-left"
        size={Size.SMALL}
        onClick={onClickSecondaryButton}
        text={getSecondaryButtonText()}
        type={Type.SECONDARY}
      />
    );
  };
  const getPanelText = (): string => {
    switch (publishingAction) {
      case SocialPostPublishingAction.PUBLISH:
        return getPublishSuccessText();
      case SocialPostPublishingAction.SCHEDULE:
        return getScheduleSuccessText();
      case SocialPostPublishingAction.SAVE:
        return getSaveSuccessText();
      default:
        return window.i18next.t('pmwjs_error_posting_this');
    }
  };

  const getPanelTextByLoadingState = (): string => {
    if (!publishingParamsForAccount) {
      return '';
    }

    switch (loadingState) {
      case LoadingStates.NOT_LOADED:
        if (publishingParamsForAccount.publishErrorMessage) {
          return publishingParamsForAccount.publishErrorMessage;
        }
        return window.i18next.t('pmwjs_error_posting_this');
      case LoadingStates.LOADING:
      case LoadingStates.LOADED:
      default:
        return getPanelText();
    }
  };

  const getLoadingSubText = (): string => {
    switch (loadingState) {
      case LoadingStates.NOT_LOADED:
        return '';
      case LoadingStates.LOADING:
        if (showWaitingMessage) {
          return window.i18next.t('pmwjs_files_may_take_minutes');
        }
        return '';
      case LoadingStates.LOADED:
      default:
        return '';
    }
  };

  const getConfettiElement = (): ReactElement<any> | null => {
    if (showConfetti) {
      return <OnboardingConfettiExplosion numShots={1} />;
    }

    return null;
  };

  return (
    <>
      <StatusPanel
        rightButton={getRightButton()}
        leftButton={getLeftButton()}
        escapedPanelTextString={getPanelTextByLoadingState()}
        escapedLoadingPanelTextString={getLoadingTextByPublishingAction(publishingAction)}
        escapedPanelSubtextString={getLoadingSubText()}
        isLoading={isLoading(loadingState)}
        theme={getThemeByLoadingState()}
        panelIcon={getIconByLoadingState()}
      />
      {getConfettiElement()}
    </>
  );
}
